// Columna derecha: el encabezado compartido del cliente y sus pestañas. El encabezado lee de la
// FILA del listado y del crédito vigente; cada pestaña falla por su cuenta.
import { CUSTOMER_FALLBACK_NAME } from '@/shared/lib/format/customerMention';
import { useMemo } from 'react';
import { AlertTriangle, Users } from 'lucide-react';
import { FileDrawerTabs } from '@/shared/patterns/FileDrawer';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { cn } from '@/lib/cn';
import { formatDateMx, mxDay } from '@/lib/dateHelpers';
import { installmentDueDay, nextDueInstallment } from '@/lib/installments';
import { DetailHeader, type DetailHeaderFact } from '@/shared/patterns/DetailHeader';
import { dueLineOf } from '../../creditFile/detailHeader';
import { productNameOf } from '../../creditFile/components/CreditSummaryTab';
import type { CreditCustomerCredit } from '@/api/services/loan-credit';
import type { CustomerCreditDetail } from '@/domains/sales/portal/customers/api/useCustomerCredit';
import type { ReactNode } from 'react';
import { useCustomer360, useOriginStore } from '../api/useCustomer360';
import { useCustomerDocuments } from '../api/useCustomerDocuments';
import { useCustomerKycSessions } from '../api/useCustomerKycSessions';
import type { CreditCustomerRow } from '../lib/creditCustomersFilters';
import {
    activeCreditsAnomalyNote,
    CUSTOMER_STATUS_CHIP_CLASS,
    customerStatusLabel,
    customerStatusNote,
    formatMoneyCents,
    formatPhone,
    levelMissingNote,
    NO_SEGMENTS_NOTE,
    SEGMENTS_UNREAD_LABEL,
    SEGMENTS_UNREAD_NOTE,
    UNNAMED_CUSTOMER,
    UNNAMED_CUSTOMER_NOTE,
    EMPTY_CELL,
} from '../lib/creditCustomersFormat';
import {
    CANCELLED_ROW_NOTE,
    CREDIT_ROW_GAP_LABEL,
    CREDIT_ROW_GAP_NOTE,
    creditRowFactsOf,
    creditRowStatusOf,
    hasActiveCredit,
    isInArrears,
    type CreditRowGap,
} from '../lib/creditCustomerCredit';
import { saleRetailerOf } from '../lib/saleRetailer';
import { ARCO_MODULE_PENDING_NOTE, ARCO_MODULE_PENDING_TITLE } from '../lib/customer360Gaps';
import { CustomerCreditHistoryTab } from './CustomerCreditHistoryTab';
import { CustomerDevicesTab } from './CustomerDevicesTab';
import { CustomerDocumentsTab } from './CustomerDocumentsTab';
import { CustomerPcfTab } from './CustomerPcfTab';
import { CustomerProfileTab } from './CustomerProfileTab';
import { CHIP, NoSourceValue, ReadFactBlock, ReadGap } from './CustomerFileUi';
import { SegmentPill, TierPill } from '@/shared/patterns/Pills';
import { CustomerReferralsTab } from '@/shared/patterns/CustomerReferrals';
import {
    CustomerMessagesTab,
    MessageMomentCategoryEnum,
    useCanSeeCustomerMessages,
} from '@/shared/patterns/CustomerMessages';
import { FALLBACK_COLOR_SLOT } from '@/shared/patterns/Pills/segmentColors';

export type DetailTab =
    | 'profile'
    | 'credits'
    | 'documents'
    | 'pcf'
    | 'devices'
    | 'arco'
    | 'referrals'
    | 'messages';

/**
 * Los segmentos del cliente en el hero. Con la lectura fallada va el motivo: un chip ahí afirmaría
 * una pertenencia que nadie miró, y un hueco mudo afirmaría que no pertenece a ninguno.
 */
function HeroSegments({ row }: { row: CreditCustomerRow }) {
    if (!row.segmentsRead) {
        return (
            <span className={cn(CHIP, 'gap-1 border-dashed border-border font-normal')}>
                Segmento
                <NoSourceValue gap={{ label: SEGMENTS_UNREAD_LABEL, note: SEGMENTS_UNREAD_NOTE }} />
            </span>
        );
    }
    if (row.segments.length === 0) {
        return (
            <span className={cn(CHIP, 'gap-1 border-dashed border-border font-normal')}>
                Segmento
                <ReadGap gap={{ label: 'Sin segmentos', note: NO_SEGMENTS_NOTE }} />
            </span>
        );
    }
    return (
        <>
            {/* El listado no publica el color del segmento: van todos con el slot de reserva. */}
            {row.segments.map((segment) => (
                <SegmentPill
                    key={segment.segmentId}
                    name={segment.name}
                    colorSlot={FALLBACK_COLOR_SLOT}
                />
            ))}
        </>
    );
}

/** El orden del mock (`ficha-cliente-360.js`). Las que no tienen fuente se quedan en su lugar. */
const TABS: { key: DetailTab; label: string }[] = [
    { key: 'profile', label: 'Perfil / KYC' },
    { key: 'pcf', label: 'Cuenta PCF' },
    { key: 'credits', label: 'Historial crediticio' },
    { key: 'devices', label: 'Dispositivos' },
    { key: 'documents', label: 'Documentos' },
    { key: 'arco', label: 'ARCO' },
    { key: 'referrals', label: 'Referidos' },
    { key: 'messages', label: 'Mensajes' },
];

const TAB_KEYS = new Set<string>(TABS.map((tab) => tab.key));

/** La pestaña del deep-link `?tab=`. Un valor que no conocemos no tumba la ficha. */
export function parseDetailTab(raw: string | undefined): DetailTab {
    return raw !== undefined && TAB_KEYS.has(raw) ? (raw as DetailTab) : 'profile';
}

export interface CreditCustomerDetailPanelProps {
    row: CreditCustomerRow | null;
    credit: CustomerCreditDetail;
    onRetryCredit: () => void;
    tab: DetailTab;
    onTabChange: (next: DetailTab) => void;
}

export function CreditCustomerDetailPanel(props: CreditCustomerDetailPanelProps) {
    if (!props.row) {
        return (
            <MasterDetailEmpty
                icon={<Users className="size-10" aria-hidden />}
                title="Selecciona un cliente"
                sub="Elige una fila de la izquierda para ver la ficha 360 completa."
            />
        );
    }
    // La `key` ata las lecturas de la ficha al cliente: sin ella React conservaría el estado al
    // cambiar de fila y la ficha quedaría mostrando los datos del anterior.
    return <LoadedCustomerFile {...props} row={props.row} key={props.row.retailCustomerId} />;
}

function LoadedCustomerFile({
    row,
    credit,
    onRetryCredit,
    tab: requestedTab,
    onTabChange,
}: CreditCustomerDetailPanelProps & { row: CreditCustomerRow }) {
    const summary = useCustomer360(row.retailCustomerId);
    const customer = summary.data?.customer;
    // La cadena sale de la venta del crédito: `customer.retailerId` es el del tenant, no el del negocio.
    const saleRetailer = saleRetailerOf(credit);
    const isScopePending = credit.state === 'loading';
    const originStore = useOriginStore(
        saleRetailer,
        customer?.originStoreId ?? null,
        isScopePending,
    );
    const kycSessions = useCustomerKycSessions(row.retailCustomerId, saleRetailer, isScopePending);
    const documents = useCustomerDocuments(row.retailCustomerId);
    // El instante contra el que se calcula la edad. Fijo mientras la ficha esté abierta: uno nuevo
    // por render daría un valor distinto en cada pintada.
    const now = useMemo(() => new Date(), []);
    const canSeeMessages = useCanSeeCustomerMessages(MessageMomentCategoryEnum.CREDIT);
    // Un `?tab=messages` sin permiso cae en Perfil en vez de dejar el cajón vacío.
    const tab = requestedTab === 'messages' && !canSeeMessages ? 'profile' : requestedTab;
    const tabs = useMemo(
        () => (canSeeMessages ? TABS : TABS.filter((entry) => entry.key !== 'messages')),
        [canSeeMessages],
    );

    return (
        <>
            <CustomerHero
                row={row}
                credit={credit}
                originStoreName={originStore.name}
                customerCreatedAt={customer?.createdAt ?? undefined}
                now={now}
            />
            <FileDrawerTabs
                tabs={tabs}
                activeTab={tab}
                wrap
                onChange={(key) => {
                    onTabChange(key as DetailTab);
                }}
            />
            {/* El cuerpo va más oscuro que las tarjetas: con los dos del mismo color el cajón se
                lee plano y ninguna tarjeta se despega. */}
            <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto bg-muted/15 p-3">
                {tab === 'profile' ? (
                    <CustomerProfileTab
                        row={row}
                        summary={summary}
                        originStore={originStore}
                        kycSessions={kycSessions}
                        documents={documents}
                        credit={credit}
                        now={now}
                    />
                ) : null}
                {tab === 'credits' ? (
                    <CustomerCreditHistoryTab credit={credit} onRetry={onRetryCredit} />
                ) : null}
                {tab === 'documents' ? (
                    <CustomerDocumentsTab
                        credit={credit}
                        documents={documents}
                        customerCreatedAt={customer?.createdAt ?? null}
                    />
                ) : null}
                {tab === 'pcf' ? <CustomerPcfTab summary={summary} /> : null}
                {tab === 'devices' ? (
                    <CustomerDevicesTab credit={credit} onRetryCredit={onRetryCredit} />
                ) : null}
                {tab === 'arco' ? (
                    <ReadFactBlock
                        title={ARCO_MODULE_PENDING_TITLE}
                        description={ARCO_MODULE_PENDING_NOTE}
                    />
                ) : null}
                {tab === 'referrals' ? (
                    <CustomerReferralsTab
                        retailCustomerId={row.retailCustomerId}
                        customerName={row.fullName ?? CUSTOMER_FALLBACK_NAME}
                        customerPhone={row.phone}
                        scope={{ retailerId: saleRetailer, isScopePending }}
                    />
                ) : null}
                {tab === 'messages' ? (
                    <CustomerMessagesTab
                        retailCustomerId={row.retailCustomerId}
                        category={MessageMomentCategoryEnum.CREDIT}
                    />
                ) : null}
            </div>
        </>
    );
}

/* ================================================================================
   HERO
   ================================================================================ */

function CustomerHero({
    row,
    credit,
    originStoreName,
    customerCreatedAt,
    now,
}: {
    row: CreditCustomerRow;
    credit: CustomerCreditDetail;
    /** El nombre de la tienda de alta, ya resuelto contra el directorio de negocios. */
    originStoreName: string | undefined;
    customerCreatedAt: string | undefined;
    now: Date;
}) {
    const facts = creditRowFactsOf(row.credit);
    const today = mxDay(now.toISOString());
    return (
        <DetailHeader
            name={
                row.fullName ?? (
                    <ReadGap
                        gap={{ label: UNNAMED_CUSTOMER, note: UNNAMED_CUSTOMER_NOTE }}
                        className="font-semibold"
                    />
                )
            }
            badges={
                <>
                    {facts.credit ? (
                        <TierPill
                            level={facts.credit.clientLevel}
                            label="full"
                            title={
                                facts.credit.clientLevel === null
                                    ? levelMissingNote(facts.credit.creditsCount)
                                    : undefined
                            }
                            className={
                                facts.credit.clientLevel === null ? 'cursor-help' : undefined
                            }
                        />
                    ) : null}
                    {facts.credit ? (
                        <StatusChip credit={facts.credit} />
                    ) : (
                        <span
                            title={CREDIT_ROW_GAP_NOTE[facts.gap]}
                            className={cn(
                                CHIP,
                                'cursor-help border-dashed border-border text-muted-foreground',
                            )}
                        >
                            {CREDIT_ROW_GAP_LABEL[facts.gap]}
                        </span>
                    )}
                    <HeroSegments row={row} />
                </>
            }
            subItems={[
                row.phone === null ? null : formatPhone(row.phone),
                originStoreName,
                customerCreatedAt ? `cliente desde ${formatDateMx(customerCreatedAt)}` : null,
            ]}
            idLabel="Cliente"
            idValue={row.retailCustomerId}
            facts={[
                balanceFactOf(facts),
                nextPaymentFactOf(credit, today),
                lastDeviceFactOf(credit),
            ]}
        />
    );
}

/**
 * «Deuda total» con el saldo insoluto y cuántos créditos la componen. Sin la deuda publicada queda
 * el «Saldo vigente» de siempre.
 */
function balanceFactOf(facts: ReturnType<typeof creditRowFactsOf>): DetailHeaderFact {
    const base = { key: 'balance', label: 'Saldo vigente' };
    if (!facts.credit) {
        return { ...base, value: <UnreadCreditValue gap={facts.gap} /> };
    }
    const { credit } = facts;
    const active = hasActiveCredit(credit);
    const capital =
        active && credit.outstandingBalanceCents !== null
            ? formatMoneyCents(credit.outstandingBalanceCents)
            : null;
    if (typeof credit.totalDebtCents === 'number') {
        return {
            key: 'balance',
            label: 'Deuda total',
            title: 'Capital, interés e IVA que faltan de sus créditos vigentes',
            value: active ? formatMoneyCents(credit.totalDebtCents) : 'Sin deuda',
            sub:
                capital === null ? (
                    creditsSummary(credit)
                ) : (
                    <>
                        {`Saldo insoluto ${capital} · `}
                        {creditsSummary(credit)}
                    </>
                ),
        };
    }
    return { ...base, value: capital ?? 'Sin saldo', sub: creditsSummary(credit) };
}

/** Lo que dice un dato del crédito vigente mientras su lectura no está lista. */
const CREDIT_DETAIL_NOTE: Record<Exclude<CustomerCreditDetail['state'], 'ready'>, string> = {
    loading: 'Cargando sus créditos…',
    'no-source': 'Sin fuente de créditos',
    error: 'No se pudieron leer sus créditos',
    forbidden: 'Sin permiso para ver sus créditos',
};

/** «Próximo pago»: la siguiente cuota del crédito vigente. */
function nextPaymentFactOf(credit: CustomerCreditDetail, today: string): DetailHeaderFact {
    const base = { key: 'next', label: 'Próximo pago' };
    if (credit.state !== 'ready') {
        return { ...base, value: EMPTY_CELL, sub: CREDIT_DETAIL_NOTE[credit.state] };
    }
    const next = nextDueInstallment(credit.data.installments);
    if (next === null) return { ...base, value: EMPTY_CELL, sub: 'Sin cuotas pendientes' };
    return {
        ...base,
        value: formatMoneyCents(next.totalCents),
        sub: next.dueDate
            ? dueLineOf(installmentDueDay(next.dueDate), today)
            : 'Se fecha al activar',
    };
}

/** «Último equipo»: el del crédito más reciente. */
function lastDeviceFactOf(credit: CustomerCreditDetail): DetailHeaderFact {
    const base = { key: 'device', label: 'Último equipo' };
    if (credit.state !== 'ready') {
        return { ...base, value: EMPTY_CELL, sub: CREDIT_DETAIL_NOTE[credit.state] };
    }
    const latest = credit.data.credits.at(0);
    if (latest === undefined) return { ...base, value: EMPTY_CELL, sub: 'Sin equipos financiados' };
    return {
        ...base,
        value: productNameOf(latest),
        sub: latest.imei ? `IMEI ${latest.imei}` : undefined,
    };
}

/** El chip de estado del hero, con los días de mora cuando la fila los trae. */
function StatusChip({ credit }: { credit: CreditCustomerCredit }) {
    const status = creditRowStatusOf(credit);
    return (
        <span
            title={
                status === 'CANCELLED'
                    ? CANCELLED_ROW_NOTE
                    : customerStatusNote(status, credit.arrearsDays)
            }
            className={cn(CHIP, 'cursor-help', CUSTOMER_STATUS_CHIP_CLASS[status])}
        >
            {customerStatusLabel(status, credit.arrearsDays)}
        </span>
    );
}

/** «N vigentes · M en total», con la mora si la hay. Sin ninguno vivo lo dice y no cuenta nada. */
function creditsSummary(credit: CreditCustomerCredit): ReactNode {
    if (!hasActiveCredit(credit)) {
        return <span className="italic text-muted-foreground">Sin créditos vigentes</span>;
    }
    const active = credit.activeCreditsCount;
    const arrears = isInArrears(credit) ? ' · en mora' : '';
    const summary = `${String(active)} ${active === 1 ? 'vigente' : 'vigentes'}${arrears} · ${String(credit.creditsCount)} en total`;

    // El «Saldo vigente» de al lado suma TODOS los vigentes: con más de uno hay que decirlo.
    if (active <= 1) return <span>{summary}</span>;
    return (
        <span
            title={activeCreditsAnomalyNote(active)}
            className="inline-flex cursor-help items-center gap-0.5 text-warning"
        >
            <AlertTriangle className="size-2.5 flex-none" aria-hidden />
            {summary}
        </span>
    );
}

function UnreadCreditValue({ gap }: { gap: CreditRowGap }) {
    return (
        <span title={CREDIT_ROW_GAP_NOTE[gap]} className="cursor-help italic text-muted-foreground">
            {CREDIT_ROW_GAP_LABEL[gap]}
        </span>
    );
}
